import React, { useState } from "react";
import { Mail, Phone, X } from "lucide-react";

// Stylized tile-grid cartogram of Ohio's 88 counties (col, row)
const GRID = {
  Williams: [1, 1], Fulton: [2, 1], Lucas: [3, 1], Ottawa: [4, 1], Erie: [5, 1], Lorain: [6, 1], Cuyahoga: [7, 1], Lake: [8, 1], Geauga: [9, 1], Ashtabula: [10, 1],
  Defiance: [1, 2], Henry: [2, 2], Wood: [3, 2], Sandusky: [4, 2], Huron: [5, 2], Medina: [6, 2], Summit: [7, 2], Portage: [8, 2], Trumbull: [9, 2],
  Paulding: [1, 3], Putnam: [2, 3], Hancock: [3, 3], Seneca: [4, 3], Crawford: [5, 3], Richland: [6, 3], Ashland: [7, 3], Wayne: [8, 3], Stark: [9, 3], Mahoning: [10, 3],
  "Van Wert": [1, 4], Allen: [2, 4], Hardin: [3, 4], Wyandot: [4, 4], Marion: [5, 4], Morrow: [6, 4], Knox: [7, 4], Holmes: [8, 4], Tuscarawas: [9, 4], Columbiana: [10, 4],
  Mercer: [1, 5], Auglaize: [2, 5], Shelby: [3, 5], Logan: [4, 5], Union: [5, 5], Delaware: [6, 5], Licking: [7, 5], Coshocton: [8, 5], Carroll: [9, 5], Jefferson: [10, 5],
  Darke: [1, 6], Miami: [2, 6], Champaign: [3, 6], Clark: [4, 6], Madison: [5, 6], Franklin: [6, 6], Fairfield: [7, 6], Muskingum: [8, 6], Harrison: [9, 6], Belmont: [10, 6],
  Preble: [1, 7], Montgomery: [2, 7], Greene: [3, 7], Fayette: [4, 7], Pickaway: [5, 7], Perry: [6, 7], Morgan: [7, 7], Guernsey: [8, 7], Noble: [9, 7], Monroe: [10, 7],
  Butler: [1, 8], Warren: [2, 8], Clinton: [3, 8], Highland: [4, 8], Ross: [5, 8], Hocking: [6, 8], Athens: [7, 8], Washington: [8, 8],
  Hamilton: [1, 9], Clermont: [2, 9], Brown: [3, 9], Adams: [4, 9], Pike: [5, 9], Vinton: [6, 9], Jackson: [7, 9], Meigs: [8, 9],
  Scioto: [5, 10], Lawrence: [6, 10], Gallia: [7, 10],
};

const cellStyle = (w) => {
  if (!w) return "bg-slate-100 text-slate-400";
  if (w.clients > 0) return "bg-[#2E9E6A] text-white font-bold";
  if (w.referrals > 0) return "bg-emerald-300 text-emerald-900 font-semibold";
  if (w.replied > 0) return "bg-sky-400 text-white font-semibold";
  if (w.contacted > 0) return "bg-[#BFE3ED] text-[#0E5A72]";
  return "bg-slate-100 text-slate-400";
};

const LEGEND = [
  ["bg-[#2E9E6A]", "Client start"],
  ["bg-emerald-300", "Referral received"],
  ["bg-sky-400", "Replied"],
  ["bg-[#BFE3ED]", "Contacted"],
  ["bg-slate-100", "Not yet reached"],
];

export const CountyWinMap = ({ wins = {}, contacts = [] }) => {
  const [selected, setSelected] = useState(null);
  const countyContacts = selected ? contacts.filter((c) => (c.county || "").trim() === selected) : [];

  return (
    <div data-testid="county-win-map" className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h4 className="font-display font-bold text-[#0E2A33] text-sm">🗺️ Ohio County Wins (stylized map — click a county to see its contacts)</h4>
        <div className="flex items-center gap-3 flex-wrap">
          {LEGEND.map(([c, label]) => (
            <span key={label} className="flex items-center gap-1.5 text-[10px] text-slate-500">
              <span className={`h-3 w-3 rounded ${c} inline-block border border-slate-200`} /> {label}
            </span>
          ))}
        </div>
      </div>
      <div className="grid gap-1" style={{ gridTemplateColumns: "repeat(10, minmax(0, 1fr))" }}>
        {Object.entries(GRID).map(([county, [col, row]]) => {
          const w = wins[county];
          const tip = w
            ? `${county}: ${w.contacted} contacted · ${w.replied} replied · ${w.referrals} referrals · ${w.clients} clients`
            : `${county}: not yet reached`;
          return (
            <button
              key={county}
              title={tip}
              data-testid={`county-cell-${county.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setSelected(selected === county ? null : county)}
              className={`rounded text-[9px] leading-tight text-center py-1.5 px-0.5 truncate transition-all hover:ring-2 hover:ring-[#0E5A72] ${cellStyle(w)} ${selected === county ? "ring-2 ring-[#0E5A72]" : ""}`}
              style={{ gridColumn: col, gridRow: row }}
            >
              {county.length > 8 ? county.slice(0, 7) + "…" : county}
            </button>
          );
        })}
      </div>

      {selected && (
        <div data-testid="county-contacts-panel" className="mt-4 bg-[#F7FBFC] rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <h5 className="font-display font-bold text-[#0E2A33] text-sm">{selected} County — {countyContacts.length} contact{countyContacts.length !== 1 ? "s" : ""}</h5>
            <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
          </div>
          {countyContacts.length === 0 && (
            <p className="text-xs text-slate-400">No contacts in this county yet — add one in the Professional Contacts tab.</p>
          )}
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {countyContacts.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-2 bg-white rounded-lg border border-slate-100 px-3 py-2 text-xs">
                <div className="min-w-0">
                  <span className="font-medium text-[#0E2A33]">{c.org}</span>
                  <span className="text-slate-400 ml-2">{c.stage?.replace(/_/g, " ")}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {c.email && (
                    <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 text-[#0E5A72] font-medium hover:underline">
                      <Mail className="h-3.5 w-3.5" /> Email
                    </a>
                  )}
                  {c.phone && (
                    <a href={`tel:${c.phone.replace(/[^0-9]/g, "")}`} className="inline-flex items-center gap-1 text-[#2E9E6A] font-medium hover:underline">
                      <Phone className="h-3.5 w-3.5" /> {c.phone}
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
